import { useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import {
  validateCyclesInput,
  validateCalendarDaysInput,
  calculateYearsFromDates,
  computeRemainingSnapshot,
  convertRemainingCanonicalToDisplay,
  isMixedYearsCalendarLimitElapsed,
  extractSequenceFromCode,
  generateComponentCode,
  isDecimalNumericTsrUnit
} from './componentFormUtils';
import { formatTlbCycleLiveDisplay } from '../../utils/tlbCycleCounters';

export const useComponentFormHandlers = ({
  formData,
  setFormData,
  timeSinceRecords,
  setTimeSinceRecords,
  dropdownOptions,
  assemblies,
  codeManuallyEdited,
  setCodeManuallyEdited,
  isOriginalEquipment,
  setIsOriginalEquipment,
  selectedComponent,
  onClose,
  onOpenAssemblyForm,
  onSubmit
}) => {
  const navigate = useNavigate();

  // Helper function to auto-fill from assembly when Original Equipment is checked
  const autoFillFromAssembly = useCallback((assemblyId, targetFormData, shouldAutoFill = isOriginalEquipment) => {
    if (!assemblyId || !shouldAutoFill) return targetFormData;
    
    const valueStr = String(assemblyId);
    const selectedAssembly = assemblies.find(a => {
      const assemblyIdStr = a._id ? String(a._id) : null;
      return assemblyIdStr === valueStr;
    });
    
    if (!selectedAssembly) return targetFormData;
    
    const filledData = { ...targetFormData };
    
    // Auto-fill Installed Date
    if (selectedAssembly.installedOnDate) {
      const installedDate = new Date(selectedAssembly.installedOnDate);
      if (!isNaN(installedDate.getTime())) {
        filledData.installedOnDate = installedDate.toISOString().split('T')[0];
      }
    }
    
    // Auto-fill License
    if (selectedAssembly.licenseId) {
      const licenseId = typeof selectedAssembly.licenseId === 'object' && selectedAssembly.licenseId !== null
        ? (selectedAssembly.licenseId._id ? String(selectedAssembly.licenseId._id) : String(selectedAssembly.licenseId))
        : String(selectedAssembly.licenseId);
      if (licenseId) {
        filledData.licenseId = licenseId;
      }
    }
    
    // Auto-fill Position
    if (selectedAssembly.position) {
      filledData.position = selectedAssembly.position;
    }
    
    // Auto-fill Work Order Number
    if (selectedAssembly.workOrderNumber) {
      filledData.workOrderNumber = selectedAssembly.workOrderNumber;
    }
    
    // Auto-generate component code if position was auto-filled and code hasn't been manually edited
    if (selectedAssembly.position && !codeManuallyEdited && filledData.position) {
      const sequence = extractSequenceFromCode(filledData.code || '');
      const generatedCode = generateComponentCode(assemblyId, filledData.position, sequence, assemblies);
      if (generatedCode) {
        filledData.code = generatedCode;
      }
    }
    
    return filledData;
  }, [assemblies, codeManuallyEdited, isOriginalEquipment]);

  const handleChange = useCallback((field) => (e) => {
    const value = e.target.type === 'checkbox' ? e.target.checked : e.target.value;
    const updatedFormData = { ...formData, [field]: value };

    // Track manual code edits
    if (field === 'code') {
      setCodeManuallyEdited(true);
    }

    // Handle Original Equipment checkbox
    if (field === 'isOriginalEquipment') {
      setIsOriginalEquipment(value);
      // If checkbox is checked and assembly is selected, auto-fill fields
      if (value && updatedFormData.assemblyId) {
        const autoFilledData = autoFillFromAssembly(updatedFormData.assemblyId, updatedFormData, value);
        setFormData(autoFilledData);
        return;
      }
      // If checkbox is unchecked, don't clear fields (user may have edited them)
      setFormData(updatedFormData);
      return;
    }

    // Handle Initial load checkbox: update isInitialLoad and workOrderNumber in one state update
    if (field === 'isInitialLoad') {
      setFormData(prev => ({
        ...prev,
        isInitialLoad: value,
        workOrderNumber: value ? 'NA - Initial Installation' : (prev.workOrderNumber === 'NA - Initial Installation' ? '' : prev.workOrderNumber || '')
      }));
      return;
    }

    // Auto-populate ATA Chapter when Assembly is selected
    if (field === 'assemblyId' && value) {
      // Normalize the value to string for comparison
      const valueStr = String(value);
      
      const selectedAssembly = assemblies.find(a => {
        // Normalize assembly _id to string for comparison
        const assemblyIdStr = a._id ? String(a._id) : null;
        return assemblyIdStr === valueStr;
      });
      
      if (selectedAssembly && selectedAssembly.ataChapterId) {
        let ataChapterId = null;
        
        // Handle populated ataChapterId (object with _id, code, name from API)
        if (typeof selectedAssembly.ataChapterId === 'object' && selectedAssembly.ataChapterId !== null) {
          // Populated object from MongoDB populate - has _id property
          if (selectedAssembly.ataChapterId._id) {
            ataChapterId = String(selectedAssembly.ataChapterId._id);
          } else {
            // Fallback: try to use the object itself (shouldn't happen with proper populate)
            ataChapterId = String(selectedAssembly.ataChapterId);
          }
        } else {
          // Unpopulated: just an ID (string or ObjectId)
          ataChapterId = String(selectedAssembly.ataChapterId);
        }
        
        if (ataChapterId) {
          updatedFormData.ataChapterId = ataChapterId;
        }
      }
      
      // If Original Equipment checkbox is checked, auto-fill from assembly (includes code generation)
      if (isOriginalEquipment) {
        const autoFilledData = autoFillFromAssembly(value, updatedFormData);
        setFormData(autoFilledData);
        return;
      }
      
      // Auto-generate component code if not manually edited and position exists
      if (!codeManuallyEdited && updatedFormData.position) {
        const sequence = extractSequenceFromCode(updatedFormData.code || '');
        const generatedCode = generateComponentCode(value, updatedFormData.position, sequence, assemblies);
        if (generatedCode) {
          updatedFormData.code = generatedCode;
        }
      } else if (!codeManuallyEdited && !updatedFormData.position) {
        // If position is not set yet, clear the code so it can be generated when position is set
        updatedFormData.code = '';
      }
    }

    // Auto-generate component code when position changes (if assembly is selected)
    if (field === 'position' && value && updatedFormData.assemblyId && !codeManuallyEdited) {
      const sequence = extractSequenceFromCode(updatedFormData.code || '');
      const generatedCode = generateComponentCode(updatedFormData.assemblyId, value, sequence, assemblies);
      if (generatedCode) {
        updatedFormData.code = generatedCode;
      }
    } else if (field === 'position' && !value && !codeManuallyEdited) {
      // If position is cleared, clear the code
      updatedFormData.code = '';
    }

    // Auto-populate Part fields when Part Master is selected (legacy Select path)
    if (field === 'partId') {
      if (!value) {
        updatedFormData.partName = '';
      } else {
        const selectedPart = dropdownOptions.parts.find(p => {
          const partId = p._id?.toString() || p._id;
          const valueStr = value?.toString() || value;
          return partId === valueStr;
        });

        if (selectedPart) {
          updatedFormData.partNumber = selectedPart.partNumber || '';
          updatedFormData.description = selectedPart.description || '';
          updatedFormData.partName = (selectedPart.partName && String(selectedPart.partName).trim()) || '';

          if (selectedPart.serialNumber) {
            updatedFormData.serialNumber = selectedPart.serialNumber;
          }

          if (selectedPart.manufacturer) {
            const manufacturerId = selectedPart.manufacturer?._id || selectedPart.manufacturer;
            const finalManufacturerId = typeof manufacturerId === 'object' && manufacturerId._id
              ? manufacturerId._id.toString()
              : manufacturerId.toString();
            updatedFormData.manufacturerId = finalManufacturerId;
          }
        }
      }
    }

    if (field === 'partNumber') {
      updatedFormData.partName = '';
    }

    setFormData(updatedFormData);
  }, [formData, setFormData, assemblies, codeManuallyEdited, isOriginalEquipment, dropdownOptions, autoFillFromAssembly, setCodeManuallyEdited, setIsOriginalEquipment]);

  /**
   * Handle Part Master selection from Autocomplete (selected option object).
   * Uses the selected part object directly so auto-populate works for parts found via search,
   * not only those in the initial dropdownOptions.parts list.
   */
  const handlePartMasterSelect = useCallback((selectedPart) => {
    if (!selectedPart) {
      setFormData(prev => ({
        ...prev,
        partId: '',
        partNumber: '',
        partName: '',
        description: '',
        serialNumber: '',
        manufacturerId: ''
      }));
      return;
    }
    const partId = selectedPart._id?.toString() || selectedPart._id;
    setFormData(prev => {
      const next = {
        ...prev,
        partId,
        partNumber: selectedPart.partNumber || '',
        partName: (selectedPart.partName && String(selectedPart.partName).trim()) || '',
        description: selectedPart.description || ''
      };
      if (selectedPart.serialNumber != null && selectedPart.serialNumber !== '') {
        next.serialNumber = selectedPart.serialNumber;
      }
      if (selectedPart.manufacturer) {
        const manufacturerId = selectedPart.manufacturer?._id || selectedPart.manufacturer;
        const finalManufacturerId = typeof manufacturerId === 'object' && manufacturerId && manufacturerId._id
          ? manufacturerId._id.toString()
          : (manufacturerId && manufacturerId.toString()) || '';
        next.manufacturerId = finalManufacturerId;
      }
      return next;
    });
  }, [setFormData]);

  const handleAddTimeSinceRecord = useCallback(() => {
    setTimeSinceRecords([
      ...timeSinceRecords,
      {
        type: 'TSN',
        label: 'Since New (TSN)',
        elapsedTime: { value: '00:00', unit: 'Hours' },
        limit: { value: '00:00', unit: 'Hours' },
        remainingTime: { value: '00:00', unit: 'Hours' },
        installedValue: { value: '00:00', unit: 'Hours' },
        startDate: new Date().toISOString().split('T')[0],
        asOnDate: new Date().toISOString().split('T')[0]
      }
    ]);
  }, [timeSinceRecords, setTimeSinceRecords]);

  // Helper function to validate and format Years input (YY.MM format)
  const validateYearsInput = useCallback((value) => {
    if (!value) return '';
    
    // Remove any non-digit or non-dot characters
    let cleaned = value.replace(/[^\d.]/g, '');
    
    // Ensure only one decimal point
    const parts = cleaned.split('.');
    if (parts.length > 2) {
      cleaned = parts[0] + '.' + parts.slice(1).join('');
    }
    
    // If there's a decimal part, validate it
    if (parts.length === 2) {
      const wholePart = parts[0];
      const decimalPart = parts[1];
      
      // Limit whole number part to 2 digits max
      if (wholePart.length > 2) {
        cleaned = wholePart.substring(0, 2) + (decimalPart ? '.' + decimalPart : '');
      }
      
      // Limit decimal part to 2 digits and ensure it's 0-12
      if (decimalPart.length > 2) {
        cleaned = wholePart + '.' + decimalPart.substring(0, 2);
      }
      
      // Validate months (0-12)
      const months = parseInt(decimalPart) || 0;
      if (months > 12) {
        cleaned = wholePart + '.12';
      }
    } else if (parts.length === 1) {
      // Limit whole number part to 2 digits max
      if (parts[0].length > 2) {
        cleaned = parts[0].substring(0, 2);
      }
    }
    
    return cleaned;
  }, []);

  const handleUpdateTimeSinceRecord = useCallback((index, field, value) => {
    setTimeSinceRecords(prevRecords => {
      const updated = [...prevRecords];
      if (field.includes('.')) {
        const [parent, child] = field.split('.');
        updated[index][parent] = updated[index][parent] || {};
        updated[index][parent][child] = value;

        if (parent === 'remainingTime' && child === 'unit') {
          const elapsedUnit = updated[index].elapsedTime?.unit || 'Hours';
          const canonical = computeRemainingSnapshot(updated[index]);
          if (canonical) {
            const display = convertRemainingCanonicalToDisplay(
              canonical.value,
              canonical.unit,
              elapsedUnit
            );
            updated[index].remainingTime = {
              value: display.value,
              unit: display.unit
            };
          } else {
            updated[index].remainingTime = {
              ...(updated[index].remainingTime || {}),
              unit: elapsedUnit
            };
          }
        } else {
        // Keep installedValue (snapshot at installation) in sync with elapsedTime edits
        if (parent === 'elapsedTime' && child === 'value') {
          const elapsedUnit = updated[index].elapsedTime?.unit || 'Hours';
          updated[index].installedValue = updated[index].installedValue || { value: '', unit: elapsedUnit };
          updated[index].installedValue.value = value;
          if (!updated[index].installedValue.unit) {
            updated[index].installedValue.unit = elapsedUnit;
          }
        }

        // Auto-calculate Remaining Time when Limit, Elapsed Time, or dates change
        const shouldRecalculate = parent === 'limit' || parent === 'elapsedTime' || 
                                  field === 'startDate' || field === 'asOnDate' ||
                                  (parent === 'elapsedTime' && child === 'unit') ||
                                  (parent === 'limit' && child === 'unit');

        // When elapsed time or limit unit changes, sync remaining time unit and format values
        if ((parent === 'elapsedTime' && child === 'unit') || (parent === 'limit' && child === 'unit')) {
          const newUnit = value;
          // Get the current units after update
          const elapsedUnit = parent === 'elapsedTime' ? newUnit : (updated[index].elapsedTime?.unit || 'Hours');
          
          // Remaining unit: same as elapsed when limit/elapsed units match; for Years↔Calendar Days mixed, keep Years/CD choice
          updated[index].remainingTime = updated[index].remainingTime || {};
          const limitUnitAfter = parent === 'limit' ? newUnit : (updated[index].limit?.unit || 'Hours');
          const elapsedUnitAfter = parent === 'elapsedTime' ? newUnit : (updated[index].elapsedTime?.unit || 'Hours');
          const mixedYearsCd = isMixedYearsCalendarLimitElapsed(limitUnitAfter, elapsedUnitAfter);
          updated[index].remainingTime.unit = elapsedUnitAfter;
          // Sync installedValue and value-at-installation units to match elapsed time unit
          updated[index].installedValue = updated[index].installedValue || { value: '', unit: elapsedUnit };
          updated[index].installedValue.unit = elapsedUnit;
          updated[index].airframeValueAtInstallation = updated[index].airframeValueAtInstallation || { value: '', unit: elapsedUnit };
          updated[index].airframeValueAtInstallation.unit = elapsedUnit;
          updated[index].parentAssemblyValueAtInstallation = updated[index].parentAssemblyValueAtInstallation || { value: '', unit: elapsedUnit };
          updated[index].parentAssemblyValueAtInstallation.unit = elapsedUnit;
          
          // Reset value format based on unit type when switching (skip remaining while mixed Years/CD — recalculated below)
          if (newUnit === 'Hours') {
            // Convert to HH:MM format if needed
            if (parent === 'elapsedTime' && updated[index].elapsedTime.value && !updated[index].elapsedTime.value.includes(':')) {
              updated[index].elapsedTime.value = '00:00';
            }
            if (parent === 'limit' && updated[index].limit.value && !updated[index].limit.value.includes(':')) {
              updated[index].limit.value = '00:00';
            }
            if (!mixedYearsCd && updated[index].remainingTime.value && !updated[index].remainingTime.value.includes(':')) {
              updated[index].remainingTime.value = '00:00';
            }
          } else if (newUnit === 'Years') {
            // Convert to YY.MM format for Years
            if (parent === 'elapsedTime' && updated[index].elapsedTime.value && updated[index].elapsedTime.value.includes(':')) {
              updated[index].elapsedTime.value = '0.00';
            }
            if (parent === 'limit' && updated[index].limit.value && updated[index].limit.value.includes(':')) {
              updated[index].limit.value = '0.00';
            }
            if (!mixedYearsCd && updated[index].remainingTime.value && updated[index].remainingTime.value.includes(':')) {
              updated[index].remainingTime.value = '0.00';
            }
          } else if (newUnit === 'Calendar Days' || newUnit === 'Landings' || isDecimalNumericTsrUnit(newUnit)) {
            const isIntegerUnit = (newUnit === 'Calendar Days' || newUnit === 'Landings');
            if (parent === 'elapsedTime' && updated[index].elapsedTime.value && updated[index].elapsedTime.value.includes(':')) {
              updated[index].elapsedTime.value = '0';
            }
            if (parent === 'limit' && updated[index].limit.value && updated[index].limit.value.includes(':')) {
              updated[index].limit.value = '0';
            }
            if (!mixedYearsCd && updated[index].remainingTime.value && updated[index].remainingTime.value.includes(':')) {
              updated[index].remainingTime.value = '0';
            }
            // Convert from Years format (YY.MM) to numeric: integer for Calendar Days/Landings, allow decimals for Cycles
            if (parent === 'elapsedTime' && updated[index].elapsedTime.value && updated[index].elapsedTime.value.includes('.')) {
              updated[index].elapsedTime.value = isIntegerUnit ? Math.floor(parseFloat(updated[index].elapsedTime.value) || 0).toString() : '0';
            }
            if (parent === 'limit' && updated[index].limit.value && updated[index].limit.value.includes('.')) {
              updated[index].limit.value = isIntegerUnit ? Math.floor(parseFloat(updated[index].limit.value) || 0).toString() : '0';
            }
            if (!mixedYearsCd && updated[index].remainingTime.value && updated[index].remainingTime.value.includes('.') && isIntegerUnit) {
              updated[index].remainingTime.value = Math.floor(parseFloat(updated[index].remainingTime.value) || 0).toString();
            }
          }

          const limitU = updated[index].limit?.unit || 'Hours';
          const elapsedU = updated[index].elapsedTime?.unit || 'Hours';
          const remainingU = updated[index].remainingTime?.unit || elapsedU;
          const rowStillCycles = [limitU, elapsedU, remainingU].some((u) => String(u).trim() === 'Cycles');
          if (!rowStillCycles) {
            delete updated[index].tlbCarriedForwardKey;
          }
        }

        if (shouldRecalculate) {
          const elapsedUnit = updated[index].elapsedTime?.unit || 'Hours';
          const limitUnit = updated[index].limit?.unit || 'Hours';
          const canonical = computeRemainingSnapshot(updated[index]);
          if (canonical) {
            const display = convertRemainingCanonicalToDisplay(
              canonical.value,
              canonical.unit,
              elapsedUnit
            );
            updated[index].remainingTime = {
              value: display.value,
              unit: display.unit
            };
          }
        }
        }
      } else {
        updated[index][field] = value;

        // Recalculate if dates changed
        if (field === 'startDate' || field === 'asOnDate') {
          const elapsedUnit = updated[index].elapsedTime?.unit || 'Hours';
          const limitUnit = updated[index].limit?.unit || 'Hours';
          if (elapsedUnit === 'Years' && updated[index].startDate && updated[index].asOnDate) {
            // Calculate elapsed time in YY.MM format
            const elapsedValueStr = calculateYearsFromDates(updated[index].startDate, updated[index].asOnDate);
            updated[index].elapsedTime = {
              value: elapsedValueStr,
              unit: elapsedUnit
            };

            const canonical = computeRemainingSnapshot(updated[index]);
            if (canonical) {
              const display = convertRemainingCanonicalToDisplay(
                canonical.value,
                canonical.unit,
                elapsedUnit
              );
              updated[index].remainingTime = {
                value: display.value,
                unit: display.unit
              };
            }
          }
        }
      }
      
      // Create a new record object to ensure React detects the change (for React.memo)
      updated[index] = { ...updated[index] };
      
      // Update formData with the new records
      setFormData(prevFormData => ({ ...prevFormData, timeSinceRecords: updated }));
      
      return updated;
    });
  }, [setFormData]);

  const handleSelectTlbCycle = useCallback((index, option) => {
    if (!option || !option.key) return;
    setTimeSinceRecords((prevRecords) => {
      const updated = [...prevRecords];
      const row = { ...updated[index] };
      const unit = 'Cycles';

      row.tlbCarriedForwardKey = option.key;
      row.label = option.label || option.key;

      row.limit = row.limit || { value: '', unit };
      row.elapsedTime = row.elapsedTime || { value: '0', unit };
      row.remainingTime = row.remainingTime || { value: '0', unit };
      row.limit.unit = unit;
      row.elapsedTime.unit = unit;
      row.remainingTime.unit = unit;

      const liveDisplay = formatTlbCycleLiveDisplay(option.liveRaw);
      if (liveDisplay !== '—') {
        row.airframeValueAtInstallation = {
          value: liveDisplay,
          unit
        };
      }

      const canonical = computeRemainingSnapshot(row);
      if (canonical) {
        row.remainingTime = {
          value: canonical.value,
          unit: canonical.unit
        };
      }

      updated[index] = row;
      setFormData((prevFormData) => ({ ...prevFormData, timeSinceRecords: updated }));
      return updated;
    });
  }, [setFormData]);

  const handleRemoveTimeSinceRecord = useCallback((index) => {
    setTimeSinceRecords(prevRecords => {
      const updated = prevRecords.filter((_, i) => i !== index);
      setFormData(prevFormData => ({ ...prevFormData, timeSinceRecords: updated }));
      return updated;
    });
  }, [setFormData, setTimeSinceRecords]);

  // Navigation handlers for adding missing master data
  const handleNavigateToAddAssembly = useCallback(() => {
    if (onOpenAssemblyForm) {
      const confirmed = window.confirm(
        'You will be taken to add a new Assembly.\n\n' +
        'After adding the Assembly, please return to this form - the dropdown will be refreshed automatically.'
      );
      if (confirmed) {
        onClose(); // Close component form
        onOpenAssemblyForm(); // Open assembly form
      }
    }
  }, [onOpenAssemblyForm, onClose]);

  const handleNavigateToAddPart = useCallback(() => {
    const confirmed = window.confirm(
      'You will be taken to Parts Catalog to add a new Part.\n\n' +
      'After adding the Part, please return to this form - the dropdown will be refreshed automatically.'
    );
    if (confirmed) {
      navigate('/parts-catalog', { 
        state: { 
          returnTo: '/aircraft-lifecycle',
          refreshDropdowns: true
        } 
      });
    }
  }, [navigate]);

  const handleNavigateToMasterData = useCallback((type, tabIndex, subTabIndex = null) => {
    const typeNames = {
      'ata-chapters': 'ATA Chapter',
      'monitor-types': 'Monitor Type',
      'licenses': 'License',
      'manufacturer-names': 'Manufacturer'
    };
    const typeName = typeNames[type] || type;
    
    const confirmed = window.confirm(
      `You will be taken to Master Data Management to add a new ${typeName}.\n\n` +
      `After adding the ${typeName}, please return to this form - the dropdown will be refreshed automatically.`
    );
    
    if (confirmed) {
      const state = { 
        openTab: tabIndex,
        returnTo: '/aircraft-lifecycle',
        refreshDropdowns: true
      };
      
      if (subTabIndex !== null) {
        state.openSubTab = subTabIndex;
      }
      
      navigate('/master-data', { state });
    }
  }, [navigate]);

  // Clean and format timeSinceRecords before submission
  const prepareTimeSinceRecordsForSubmission = useCallback((records) => {
    if (!records || records.length === 0) return [];
    
    return records.map(record => {
      // Ensure required fields have defaults
      const elapsedUnit = record.elapsedTime?.unit || 'Hours';
      const elapsedValue = record.elapsedTime?.value || (
        elapsedUnit === 'Hours' ? '00:00' 
        : elapsedUnit === 'Years' ? '0.00' 
        : (elapsedUnit === 'Calendar Days' || elapsedUnit === 'Landings' || isDecimalNumericTsrUnit(elapsedUnit)) ? '0'
        : '0.00'
      );
      const remainingUnit = record.remainingTime?.unit || elapsedUnit;
      const remainingValue = record.remainingTime?.value || (
        remainingUnit === 'Hours' ? '00:00' 
        : remainingUnit === 'Years' ? '0.00' 
        : (remainingUnit === 'Calendar Days' || remainingUnit === 'Landings' || isDecimalNumericTsrUnit(remainingUnit)) ? '0'
        : '0.00'
      );
      
      // Convert dates to Date objects, then to ISO strings for API
      let startDate = new Date();
      let asOnDate = new Date();
      try {
        if (record.startDate) {
          const parsedStart = new Date(record.startDate);
          if (!isNaN(parsedStart.getTime())) startDate = parsedStart;
        }
        if (record.asOnDate) {
          const parsedAsOn = new Date(record.asOnDate);
          if (!isNaN(parsedAsOn.getTime())) asOnDate = parsedAsOn;
        }
      } catch (e) {
        console.error('Error parsing dates:', e);
      }

      // Clean numeric units: Calendar Days/Landings = integer; Cycles = up to 2 decimals
      let cleanedElapsedValue = String(elapsedValue).trim();
      let cleanedRemainingValue = String(remainingValue).trim();
      
      if (elapsedUnit === 'Calendar Days' || elapsedUnit === 'Landings') {
        cleanedElapsedValue = Math.floor(parseFloat(cleanedElapsedValue) || 0).toString();
      } else if (isDecimalNumericTsrUnit(elapsedUnit)) {
        cleanedElapsedValue = (Math.round(parseFloat(cleanedElapsedValue) * 100) / 100).toString();
      }
      if (remainingUnit === 'Calendar Days' || remainingUnit === 'Landings') {
        cleanedRemainingValue = Math.floor(parseFloat(cleanedRemainingValue) || 0).toString();
      } else if (isDecimalNumericTsrUnit(remainingUnit)) {
        cleanedRemainingValue = (Math.round(parseFloat(cleanedRemainingValue) * 100) / 100).toString();
      }

      const cleaned = {
        type: record.type || 'TSN',
        label: (record.label || '').trim(),
        ...(record.tlbCarriedForwardKey && String(record.tlbCarriedForwardKey).trim()
          ? { tlbCarriedForwardKey: String(record.tlbCarriedForwardKey).trim() }
          : {}),
        elapsedTime: {
          value: cleanedElapsedValue,
          unit: String(elapsedUnit).trim()
        },
        remainingTime: {
          value: cleanedRemainingValue,
          unit: String(remainingUnit).trim()
        },
        startDate: startDate,
        asOnDate: asOnDate
      };

      if (record.installedValue && record.installedValue.value != null && String(record.installedValue.value).trim() !== '') {
        const installedUnit = (record.installedValue.unit && String(record.installedValue.unit).trim()) || String(elapsedUnit).trim();
        const installedValue = String(record.installedValue.value).trim();
        cleaned.installedValue = {
          value: installedValue,
          unit: installedUnit
        };
      }

      const defaultValAtInstallUnit = String(record.limit?.unit || elapsedUnit).trim();
      const normalizeValueAtInstallation = (val) => {
        if (!val || val.value == null || String(val.value).trim() === '') return null;
        let v = String(val.value).trim();
        const u = (val.unit && String(val.unit).trim()) || defaultValAtInstallUnit;
        if (u === 'Calendar Days' || u === 'Landings') {
          v = Math.floor(parseFloat(v) || 0).toString();
        } else if (isDecimalNumericTsrUnit(u)) {
          v = (Math.round(parseFloat(v) * 100) / 100).toString();
        }
        return { value: v, unit: u };
      };

      const airframeVal = normalizeValueAtInstallation(record.airframeValueAtInstallation);
      if (airframeVal) cleaned.airframeValueAtInstallation = airframeVal;

      const parentVal = normalizeValueAtInstallation(record.parentAssemblyValueAtInstallation);
      if (parentVal) cleaned.parentAssemblyValueAtInstallation = parentVal;

      // Add limit only if it has a valid value (limit is optional in schema)
      // Only include limit if both value and unit are present and value is not empty/default
      let limitValue = record.limit?.value ? String(record.limit.value).trim() : '';
      const limitUnit = record.limit?.unit ? String(record.limit.unit).trim() : '';
      
      if (limitUnit === 'Calendar Days' || limitUnit === 'Landings') {
        limitValue = limitValue ? Math.floor(parseFloat(limitValue) || 0).toString() : limitValue;
      } else if (isDecimalNumericTsrUnit(limitUnit)) {
        limitValue = limitValue ? (Math.round(parseFloat(limitValue) * 100) / 100).toString() : limitValue;
      }
      
      if (limitValue && limitValue !== '' && limitValue !== '0.00' && limitValue !== '00:00' && limitUnit) {
        cleaned.limit = {
          value: limitValue,
          unit: limitUnit
        };
      }
      // If limit is not valid, don't include it (schema allows it to be optional)

      return cleaned;
    });
  }, []);

  const handleSubmit = useCallback(async () => {
    try {
      // Clean and format timeSinceRecords before submission
      const cleanedTimeSinceRecords = prepareTimeSinceRecordsForSubmission(timeSinceRecords);
      
      // Prepare submission data
      const submitData = { ...formData };
      
      // Map manufacturerNameId to manufacturerId if needed
      // Component model uses Manufacturer (not ManufacturerName) for backward compatibility
      // The form uses manufacturerNames dropdown, so manufacturerId contains a ManufacturerName ID
      if (submitData.manufacturerId && dropdownOptions.manufacturerNames) {
        // Check if manufacturerId is actually a ManufacturerName ID
        const selectedManufacturerName = dropdownOptions.manufacturerNames.find(
          mn => mn._id?.toString() === submitData.manufacturerId?.toString()
        );
        
        if (selectedManufacturerName) {
          // Find the matching Manufacturer record from Master Data only (single source of truth)
          try {
            const manufacturersResponse = await axios.get('/api/master-data/manufacturers', {
              params: { isActive: 'true' }
            });
            const manufacturerNameIdStr = String(selectedManufacturerName._id);
            let matchingManufacturer = manufacturersResponse.data?.find(m => {
              const mfrNameId = m.manufacturerName?._id?.toString() || m.manufacturerName?.toString();
              return mfrNameId === manufacturerNameIdStr;
            });
            if (!matchingManufacturer) {
              const allManufacturersResponse = await axios.get('/api/master-data/manufacturers');
              matchingManufacturer = allManufacturersResponse.data?.find(m => {
                const mfrNameId = m.manufacturerName?._id?.toString() || m.manufacturerName?.toString();
                return mfrNameId === manufacturerNameIdStr;
              });
              if (matchingManufacturer && !matchingManufacturer.isActive) {
                throw new Error(`Manufacturer "${selectedManufacturerName.name}" exists but is inactive. Please activate it in Master Data Management or select a different manufacturer.`);
              }
            }
            if (matchingManufacturer) {
              submitData.manufacturerId = matchingManufacturer._id;
            } else {
              throw new Error(
                `Manufacturer record not found for "${selectedManufacturerName.name}".\n\n` +
                `Manufacturer Name is registered in Master Data first. Create the Manufacturer record there:\n` +
                `1. Go to: Master Data Management > Manufacturer Setup\n` +
                `2. Add a new Manufacturer\n` +
                `3. Select Manufacturer Name: "${selectedManufacturerName.name}" (from dropdown)\n` +
                `4. Set Category and save\n\n` +
                `Then try adding the component again.`
              );
            }
          } catch (error) {
            console.error('Error fetching manufacturer for mapping:', error);
            throw new Error(error.message || `Failed to map manufacturer: ${error.message}`);
          }
        }
      }
      
      // Validate manufacturerId is a valid MongoDB ObjectId format if set
      if (submitData.manufacturerId) {
        const objectIdPattern = /^[0-9a-fA-F]{24}$/;
        if (!objectIdPattern.test(submitData.manufacturerId)) {
          throw new Error('Invalid manufacturer ID format. Please select a manufacturer from the dropdown.');
        }
      }
      
      // Ensure installedOnDate is a Date object
      if (submitData.installedOnDate) {
        if (typeof submitData.installedOnDate === 'string') {
          const date = new Date(submitData.installedOnDate);
          if (!isNaN(date.getTime())) {
            submitData.installedOnDate = date;
          } else {
            // Invalid date, use current date as fallback
            submitData.installedOnDate = new Date();
          }
        }
        // If it's already a Date object, keep it as is
      }
      
      // Add cleaned timeSinceRecords
      submitData.timeSinceRecords = cleanedTimeSinceRecords;

      delete submitData.partName;

      // Remove any undefined or null values that might cause issues
      Object.keys(submitData).forEach(key => {
        if (submitData[key] === undefined || submitData[key] === null) {
          delete submitData[key];
        }
      });
      
      onSubmit(submitData);
    } catch (error) {
      console.error('Error preparing component data:', error);
      alert(error.message || 'Error preparing form data. Please check all fields are filled correctly.');
    }
  }, [timeSinceRecords, formData, dropdownOptions, prepareTimeSinceRecordsForSubmission, onSubmit]);

  return {
    handleChange,
    handlePartMasterSelect,
    handleAddTimeSinceRecord,
    handleUpdateTimeSinceRecord,
    handleSelectTlbCycle,
    handleRemoveTimeSinceRecord,
    validateYearsInput,
    validateCyclesInput,
    validateCalendarDaysInput,
    handleNavigateToAddAssembly,
    handleNavigateToAddPart,
    handleNavigateToMasterData,
    handleSubmit
  };
};
